import { useClientPortalAPI } from "@certn/api";
import { Container, Divider, Stack, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ComponentProps, Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

import constants, { UNIFIED_WORKSPACE_ID } from "../../../constants";
import { AtsIntegrationRow } from "./AtsIntegrationRow";
import { AtsIntegrationRowSkeleton } from "./AtsIntegrationRowSkeleton";
import greenhouse from "./icons/greenhouse.png";
import icims from "./icons/icims.png";
import lever from "./icons/lever.png";
import pageup from "./icons/pageup.png";
import sapsf from "./icons/sapsf.png";
import workable from "./icons/workable.png";
import workday from "./icons/workday.png";
import { WORKDAY_DEPLOYMENT_GUIDE_URL } from "./workday/constants";

type UnifiedIntegration = {
  type: string;
  logo_url: string;
  name: string;
  description: string;
};

type AtsIntegrationListInnerProps = {
  unifiedIntegrations?: UnifiedIntegration[];
  isUnifiedLoading?: boolean;
};

function UnifiedIntegrationList({
  children,
}: {
  children: (props: AtsIntegrationListInnerProps) => ReactNode;
}) {
  const env = constants.MODE === "production" ? "Production" : "Staging";
  const { data: unifiedIntegrations, isLoading: isUnifiedLoading } = useSWR<
    UnifiedIntegration[]
  >(
    `https://api.unified.to/unified/integration/workspace/${UNIFIED_WORKSPACE_ID}?env=${env}`,
    (url: string) => fetch(url).then((res) => res.json())
  );

  return children({ unifiedIntegrations, isUnifiedLoading });
}

type IntegrationRowModel = {
  key: string;
  props: ComponentProps<typeof AtsIntegrationRow>;
};

const sortByName = (a: IntegrationRowModel, b: IntegrationRowModel) =>
  (a.props.name ?? "").localeCompare(b.props.name ?? "", undefined, {
    sensitivity: "base",
  });

function IntegrationGroup({ rows }: { rows: IntegrationRowModel[] }) {
  return (
    <Stack className={styles.list}>
      {rows.map((row, idx) => (
        <Fragment key={row.key}>
          {idx > 0 && <Divider />}
          <AtsIntegrationRow {...row.props} />
        </Fragment>
      ))}
    </Stack>
  );
}

export function AtsIntegrationListInner({
  unifiedIntegrations,
  isUnifiedLoading,
}: AtsIntegrationListInnerProps) {
  const { t } = useTranslation();
  const {
    clientPortalEnableWorkdayAtsIntegration,
    clientPortalEnablePageupAtsIntegration,
    clientPortalEnableSuccessFactorsAtsIntegration,
    clientPortalEnableGreenhouseAtsIntegration,
    clientPortalEnableWorkableAtsIntegration,
    clientPortalEnableIcimsAtsIntegration,
  } = useFlags();

  const { data, isLoading } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/"
  );

  const leverIntegration = (data?.results || []).find(
    (result) => result.lever_integration
  );
  const workdayIntegration = (data?.results || []).find(
    (result) => result.workday_integration
  );
  const unifiedIntegrationsList = (data?.results || []).filter(
    (result) => result.unified_integration
  );
  const pageupIntegration = (data?.results || []).find(
    (result) => result.pageup_integration
  );
  const successFactorsIntegration = (data?.results || []).find(
    (result) => result.successfactors_integration
  );
  const greenhouseIntegration = (data?.results || []).find(
    (result) => result.greenhouse_integration
  );
  const workableIntegration = (data?.results || []).find(
    (result) => result.workable_integration
  );
  const icimsIntegration = (data?.results || []).find(
    (result) => result.icims_integration
  );

  const rows: IntegrationRowModel[] = [
    {
      key: "lever",
      props: {
        exists: !!leverIntegration,
        isEnabled: leverIntegration?.is_active,
        type: "lever",
        icon: lever,
        name: "Lever",
        description: t(
          "Connect with Lever for a seamless candidate experience."
        ),
        supportLink: {
          text: t("Learn how to set up this integration."),
          url: "https://help.certn.co/hc/en-us/articles/49833985179539-Lever-ATS-Integration-Setup-Guide",
        },
      },
    },
    {
      key: "workday",
      props: {
        comingSoon: !clientPortalEnableWorkdayAtsIntegration,
        exists: !!workdayIntegration,
        isEnabled: workdayIntegration?.is_active,
        type: "workday",
        icon: workday,
        name: "Workday",
        description: t(
          "Connect with Workday, an enterprise cloud application for finance and human resources."
        ),
        supportLink: {
          text: t("Learn how to set up this integration."),
          url: WORKDAY_DEPLOYMENT_GUIDE_URL,
        },
      },
    },
    {
      key: "greenhouse",
      props: {
        comingSoon: !clientPortalEnableGreenhouseAtsIntegration,
        exists: !!greenhouseIntegration,
        isEnabled: greenhouseIntegration?.is_active,
        type: "greenhouse",
        icon: greenhouse,
        name: "Greenhouse",
        description: t(
          "Connect with Greenhouse, a hiring operating system for people-first companies."
        ),
        supportLink: {
          text: t("Learn how to set up this integration."),
          url: "https://help.certn.co/hc/en-us/articles/49116069848851-Greenhouse-Certn-Integration-Setup",
        },
      },
    },
    {
      key: "workable",
      props: {
        comingSoon: !clientPortalEnableWorkableAtsIntegration,
        exists: !!workableIntegration,
        isEnabled: workableIntegration?.is_active,
        type: "workable",
        icon: workable,
        name: "Workable",
        description: t(
          "Redefining HR with smart tools for hiring, data management, time tracking, and payroll."
        ),
      },
    },
    ...(unifiedIntegrations || []).map((integration) => {
      const integrationData = unifiedIntegrationsList.find(
        ({ unified_integration }) =>
          unified_integration?.type === integration.type
      );
      return {
        key: `unified-${integration.type}`,
        props: {
          exists: !!integrationData,
          isEnabled: integrationData?.is_active,
          type: `unified/${integration.type}`,
          icon: integration.logo_url,
          name: integration.name,
          description: integration.description,
        },
      };
    }),
    {
      key: "pageup",
      props: {
        comingSoon: !clientPortalEnablePageupAtsIntegration,
        exists: !!pageupIntegration,
        isEnabled: pageupIntegration?.is_active,
        type: "pageup",
        icon: pageup,
        name: "PageUp",
        description: t(
          "Connect with PageUp, an AI-powered talent acquisition platform."
        ),
        supportLink: {
          text: t("Learn how to set up this integration."),
          url: "https://help.certn.co/hc/en-us/articles/49832279603219-Pageup-Technical-Integration-Guide",
        },
      },
    },
    {
      key: "successfactors",
      props: {
        comingSoon: !clientPortalEnableSuccessFactorsAtsIntegration,
        exists: !!successFactorsIntegration,
        isEnabled: successFactorsIntegration?.is_active,
        type: "successfactors",
        icon: sapsf,
        name: "SAP SuccessFactors",
        description: t("Cloud-based HR and talent management platform."),
        supportLink: {
          text: t("Learn how to set up this integration."),
          url: "https://help.certn.co/hc/en-us/articles/56301356500371-SAP-SuccessFactors-Certn-integration-setup",
        },
      },
    },
    {
      key: "icims",
      props: {
        comingSoon: !clientPortalEnableIcimsAtsIntegration,
        exists: !!icimsIntegration,
        isEnabled: icimsIntegration?.is_active,
        type: "icims",
        icon: icims,
        name: "iCIMS",
        description: t(
          "Connect with iCIMS, powering exceptional hiring for enterprises in an AI-first world."
        ),
        supportLink: {
          text: t("Learn how to set up this integration."),
          url: "https://help.certn.co/hc/en-us/articles/50495958396307-ICIMS-Certn-Integration-Setup",
        },
      },
    },
  ];

  const availableRows = rows
    .filter((r) => !r.props.comingSoon)
    .sort(sortByName);
  const comingSoonRows = rows
    .filter((r) => r.props.comingSoon)
    .sort(sortByName);

  return (
    <Container layout="full-width">
      <Stack gap="space-0">
        <Text style="global/heading-large">
          {t("ATS & HR Platform Integrations")}
        </Text>
        <Text>
          {t(
            "Configure your integrations with leading applicant tracking systems and HR platforms."
          )}
        </Text>
      </Stack>
      {isLoading || isUnifiedLoading ? (
        <Stack className={styles.list}>
          {[...Array(rows.length)].map((_, i) => (
            <AtsIntegrationRowSkeleton key={i} />
          ))}
        </Stack>
      ) : (
        <>
          {availableRows.length > 0 && (
            <Stack gap="space-2">
              <Text style="global/heading-medium">{t("Available")}</Text>
              <IntegrationGroup rows={availableRows} />
            </Stack>
          )}
          {comingSoonRows.length > 0 && (
            <Stack gap="space-2">
              <Text style="global/heading-medium">{t("Coming soon")}</Text>
              <IntegrationGroup rows={comingSoonRows} />
            </Stack>
          )}
        </>
      )}
    </Container>
  );
}

const styles = {
  list: css({
    rowGap: "0 !important",
    border: "solid 1px var(--border-base)",
    borderRadius: "var(--border-radius-2)",
  }),
};

export function AtsIntegrationList() {
  const { clientPortalEnableUnifiedAtsIntegration } = useFlags();

  return clientPortalEnableUnifiedAtsIntegration ? (
    <UnifiedIntegrationList>
      {(props) => <AtsIntegrationListInner {...props} />}
    </UnifiedIntegrationList>
  ) : (
    <AtsIntegrationListInner />
  );
}
